import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { AgendaItem } from '@/types';
import { Badge, BadgeTone } from '@/components/ui/Badge';
import { TONE_DOT, TYPE_LABEL } from './EventPill';

interface Props {
  events: AgendaItem[];
  onDayClick: (d: Date) => void;
}

const TONE_BADGE: Record<AgendaItem['type'], BadgeTone> = {
  edital:   'primary',
  certidao: 'warning',
  contrato: 'success',
  sessao:   'info',
};

const MONTH_ABBR = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

export function ListView({ events, onDayClick }: Props) {
  // Agrupa por dia e ordena
  const groups = useMemo(() => {
    const m: Record<string, { date: Date; events: AgendaItem[] }> = {};
    events.forEach(e => {
      const d = new Date(e.date);
      const key = d.toDateString();
      if (!m[key]) m[key] = { date: d, events: [] };
      m[key].events.push(e);
    });
    return Object.values(m)
      .sort((a, b) => +a.date - +b.date)
      .map(g => ({
        ...g,
        events: g.events.sort((a, b) => (a.hour ?? 0) - (b.hour ?? 0)),
      }));
  }, [events]);

  if (groups.length === 0) {
    return (
      <div className="py-20 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
        Nenhum compromisso no período.
      </div>
    );
  }

  return (
    <div>
      {groups.map((g, gi) => {
        const day = String(g.date.getDate()).padStart(2, '0');
        const monthAbbr = MONTH_ABBR[g.date.getMonth()];
        const weekday = g.date.toLocaleDateString('pt-BR', { weekday: 'long' });

        return (
          <section key={gi} className={gi === 0 ? '' : 'mt-6'}>
            <header className="flex items-baseline gap-3 mb-2">
              <div className="flex flex-col items-center w-[52px] h-[52px] rounded-lg border border-line dark:border-line-dark flex-shrink-0">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark pt-1">
                  {monthAbbr}
                </div>
                <div className="text-[20px] font-extralight tabular-nums text-ink-title dark:text-ink-title-dark -mt-0.5">
                  {day}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-ink-title dark:text-ink-title-dark capitalize">
                  {weekday}
                </div>
                <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                  {g.events.length} compromisso{g.events.length > 1 ? 's' : ''}
                </div>
              </div>
            </header>

            <div className="divide-y divide-line dark:divide-line-dark">
              {g.events.map(e => {
                const hh = String(e.hour ?? 9).padStart(2, '0');
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onDayClick(g.date)}
                    className="w-full grid grid-cols-[56px_1fr_auto_auto] gap-3 items-center py-3 hover:bg-surface-alt dark:hover:bg-surface-dark-alt transition-colors text-left px-2 rounded-md"
                  >
                    <span className="tabular-nums text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                      {hh}h
                    </span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TONE_DOT[e.type]}`} />
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-ink-title dark:text-ink-title-dark truncate">
                          {e.title}
                        </div>
                        {e.subtitle && (
                          <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark truncate">
                            {e.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge tone={TONE_BADGE[e.type]}>{TYPE_LABEL[e.type]}</Badge>
                    <ChevronRight size={14} className="text-ink-placeholder dark:text-ink-placeholder-dark" />
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
